import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTheme } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import {
  AppCurrentVisits,
  AppConversionRates,
  AppWidgetSummary,
} from "../sections/@dashboard/app";
import { get } from "src/utils/networkUtils";

// Forwards are stored (and shown to fans) as "STR".
const POSITIONS = ["GK", "DEF", "MID", "STR"];

const n = (value) => Number(value ?? 0).toLocaleString();

export default function DashboardAppPage() {
  const theme = useTheme();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    let ignore = false;
    setLoading(true);
    setError(null);
    get("/admin/reports/summary")
      .then((r) => {
        if (!ignore) setSummary(r?.data ?? null);
      })
      .catch((err) => {
        if (ignore) return;
        setError(
          err?.response?.data?.message ??
            err?.message ??
            "Failed to load the report"
        );
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  const tournament = summary?.tournament ?? null;
  const players = summary?.players;
  const leagues = summary?.leagues;
  const fixtures = summary?.fixtures;

  const positionData = players
    ? [
        ...POSITIONS.map((pos) => ({
          label: pos,
          value: players.byPosition[pos],
        })),
        { label: "Other", value: players.byPosition.other },
      ].filter((item) => item.value > 0)
    : [];

  const topPlayers = (summary?.topPlayers ?? []).map((p) => ({
    label: p.displayName,
    value: Math.round(p.totalPoints),
  }));

  return (
    <>
      <Helmet>
        <title> Reports | EngageCraft Fantasy Football Admin </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>
          EngageCraft Fantasy Football - Reports
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {tournament
            ? `Active tournament: ${[tournament.name, tournament.seasonLabel]
                .filter(Boolean)
                .join(" ")}`
            : "Figures for the active tournament."}
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={load}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : summary && !tournament ? (
          <Alert severity="info">
            No tournament is active, so there is nothing to report yet.
          </Alert>
        ) : summary ? (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title="Players"
                total={players.total}
                icon={"mdi:account-group"}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title="Leagues"
                total={leagues.total}
                caption={`Mini ${n(leagues.byType.mini_league)} · Club ${n(
                  leagues.byType.club
                )} · National ${n(leagues.byType.national)}`}
                color="info"
                icon={"mdi:trophy"}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title="Fixtures"
                total={fixtures.total}
                color="warning"
                icon={"mdi:soccer-field"}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title="Completed Fixtures"
                total={fixtures.played}
                color="success"
                icon={"mdi:check-circle"}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <AppCurrentVisits
                title="Players by Position"
                chartData={
                  positionData.length > 0
                    ? positionData
                    : [{ label: "No data", value: 1 }]
                }
                chartColors={[
                  theme.palette.primary.main,
                  theme.palette.info.main,
                  theme.palette.warning.main,
                  theme.palette.error.main,
                  theme.palette.grey[500],
                ]}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={8}>
              <AppConversionRates
                title="Top 10 Players by Total Points"
                subheader={`From ${n(players.total)} players in the tournament`}
                chartData={
                  topPlayers.length > 0
                    ? topPlayers
                    : [{ label: "No points yet", value: 0 }]
                }
              />
            </Grid>
          </Grid>
        ) : null}
      </Container>
    </>
  );
}
